import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Edit2,
  Image as ImageIcon,
  PlusCircle,
  RefreshCw,
  Trash2,
  Upload,
  Video,
  X
} from 'lucide-react';
import apiClient, { endpoints, uploadClient } from '../../api';
import './AdminAds.css';

const PLACEMENTS = [
  { id: 'home_banner', label: 'Home Top Banner (1st Banner)' },
  { id: 'home_secondary_banner', label: 'Home Secondary Banner (2nd Banner)' },
  { id: 'home', label: 'Home (General)' },
  { id: 'categories', label: 'Categories' },
  { id: 'cart', label: 'Cart' },
  { id: 'checkout', label: 'Checkout' }
];

const initialForm = {
  title: '',
  link_url: '',
  placements: ['home'],
  display_order: '0',
  is_active: true,
  starts_at: '',
  ends_at: ''
};

const normalizePlacements = (value) => {
  let raw = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        raw = JSON.parse(trimmed);
      } catch (_) {
        raw = trimmed;
      }
    }
  }

  const values = Array.isArray(raw)
    ? raw
    : String(raw || '')
        .split(',')
        .map((item) => item.trim());

  const allowed = new Set(PLACEMENTS.map((placement) => placement.id));
  const placements = values
    .map((item) => String(item || '').trim().toLowerCase())
    .filter((item) => allowed.has(item));

  return placements.length ? [...new Set(placements)] : ['home'];
};

const toDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const getMediaType = (fileOrAd) => {
  if (!fileOrAd) return 'image';
  const type = fileOrAd.type || fileOrAd.mediaType || fileOrAd.media_type || '';
  if (String(type).startsWith('video')) return 'video';
  if (String(type).startsWith('image')) return 'image';
  return String(fileOrAd.mediaUrl || fileOrAd.media_url || '').match(/\.(mp4|mov|webm|m4v)(\?|$)/i)
    ? 'video'
    : 'image';
};

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [editingAd, setEditingAd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const previewType = useMemo(
    () => (mediaFile ? getMediaType(mediaFile) : getMediaType(editingAd)),
    [mediaFile, editingAd]
  );

  const formatError = (err, defaultMsg) => {
    const backendMsg = err?.response?.data?.error || err?.response?.data?.message;
    if (backendMsg) return backendMsg;
    if (err?.response?.status === 413 || (typeof err?.response?.data === 'string' && err.response.data.includes('413'))) {
      return 'The uploaded file is too large. Server rejected it (413 Payload Too Large).';
    }
    const isNetwork =
      !err.response ||
      err.message === 'Network Error' ||
      err.code === 'ERR_NETWORK' ||
      String(err.message || '').toLowerCase().includes('network');

    if (isNetwork) {
      return 'Network Error: Request was blocked or server is unreachable. If you have an AdBlocker (uBlock, AdBlock, Brave Shields) enabled in your browser, please pause/disable it for this site as it blocks ad endpoints.';
    }
    return err.message || defaultMsg;
  };

  const executeWithFallback = async (primaryCall, fallbackCall) => {
    try {
      return await primaryCall();
    } catch (err) {
      const is404 = err?.response?.status === 404;
      const isNetwork =
        !err.response ||
        err.message === 'Network Error' ||
        err.code === 'ERR_NETWORK';

      if ((is404 || isNetwork) && fallbackCall) {
        console.warn('[AdminAds] Primary request failed, attempting fallback endpoint...', err?.message || err);
        return await fallbackCall();
      }
      throw err;
    }
  };

  const fetchAds = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await executeWithFallback(
        () => apiClient.get(endpoints.adminAds),
        () => apiClient.get(endpoints.adminAdsLegacy)
      );
      setAds(data.ads || []);
    } catch (err) {
      setError(formatError(err, 'Failed to load ads'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  useEffect(() => {
    return () => {
      if (mediaPreview && mediaPreview.startsWith('blob:')) {
        URL.revokeObjectURL(mediaPreview);
      }
    };
  }, [mediaPreview]);

  const resetForm = () => {
    setForm(initialForm);
    setMediaFile(null);
    setMediaPreview('');
    setEditingAd(null);
    setError('');
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  const togglePlacement = (placement) => {
    setForm((current) => {
      const currentPlacements = normalizePlacements(current.placements);
      const exists = currentPlacements.includes(placement);
      const placements = exists
        ? currentPlacements.filter((item) => item !== placement)
        : [...currentPlacements, placement];
      return { ...current, placements: placements.length ? placements : ['home'] };
    });
  };

  const buildPayload = () => {
    const payload = new FormData();
    payload.append('title', form.title.trim());
    payload.append('link_url', form.link_url.trim());
    payload.append('placements', normalizePlacements(form.placements).join(','));
    payload.append('display_order', form.display_order || '0');
    payload.append('is_active', form.is_active ? '1' : '0');
    payload.append('starts_at', form.starts_at);
    payload.append('ends_at', form.ends_at);
    if (mediaFile) payload.append('media', mediaFile);
    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!editingAd && !mediaFile) {
      setError('Please choose an image or video file.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setMessage('');
      const payload = buildPayload();
      if (editingAd) {
        await executeWithFallback(
          () => uploadClient.put(endpoints.adminAdById(editingAd.id), payload),
          () => uploadClient.put(endpoints.adminAdByIdLegacy(editingAd.id), payload)
        );
        setMessage('Ad updated successfully.');
      } else {
        await executeWithFallback(
          () => uploadClient.post(endpoints.adminAds, payload),
          () => uploadClient.post(endpoints.adminAdsLegacy, payload)
        );
        setMessage('Ad created successfully.');
      }
      resetForm();
      await fetchAds();
    } catch (err) {
      setError(formatError(err, 'Failed to save ad'));
    } finally {
      setSaving(false);
    }
  };

  const editAd = (ad) => {
    setEditingAd(ad);
    setMediaFile(null);
    setMediaPreview(ad.mediaUrl || ad.media_url || '');
    setForm({
      title: ad.title || '',
      link_url: ad.linkUrl || ad.link_url || '',
      placements: normalizePlacements(ad.placements),
      display_order: String(ad.displayOrder ?? ad.display_order ?? 0),
      is_active: Boolean(ad.isActive ?? ad.is_active),
      starts_at: toDateTimeInput(ad.startsAt || ad.starts_at),
      ends_at: toDateTimeInput(ad.endsAt || ad.ends_at)
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (ad) => {
    try {
      setError('');
      const nextActive = !(ad.isActive ?? ad.is_active);
      await executeWithFallback(
        () => apiClient.patch(endpoints.adminAdStatus(ad.id), { is_active: nextActive }),
        () => apiClient.patch(endpoints.adminAdStatusLegacy(ad.id), { is_active: nextActive })
      );
      await fetchAds();
    } catch (err) {
      setError(formatError(err, 'Failed to update status'));
    }
  };

  const deleteAd = async (ad) => {
    if (!window.confirm(`Delete "${ad.title || 'this ad'}"?`)) return;
    try {
      setError('');
      await executeWithFallback(
        () => apiClient.delete(endpoints.adminAdById(ad.id)),
        () => apiClient.delete(endpoints.adminAdByIdLegacy(ad.id))
      );
      await fetchAds();
      if (editingAd?.id === ad.id) resetForm();
    } catch (err) {
      setError(formatError(err, 'Failed to delete ad'));
    }
  };

  const selectedPlacements = normalizePlacements(form.placements);

  return (
    <div className="admin-ads">
      <div className="admin-ads-header">
        <div>
          <h1>App Ads</h1>
          <p>Upload image or video ads and choose where they appear in the customer app.</p>
        </div>
        <button className="ads-refresh-btn" onClick={fetchAds} disabled={loading}>
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {message ? <div className="ads-alert success">{message}</div> : null}
      {error ? <div className="ads-alert error">{error}</div> : null}

      <form className="ads-editor" onSubmit={handleSubmit}>
        <div className="ads-editor-main">
          <div className="ads-editor-title">
            {editingAd ? <Edit2 size={18} /> : <PlusCircle size={18} />}
            <h2>{editingAd ? 'Edit Ad' : 'Create Ad'}</h2>
          </div>

          <div className="ads-form-grid">
            <label>
              <span>Title</span>
              <input
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
                placeholder="Weekend grocery offer"
              />
            </label>

            <label>
              <span>Link URL</span>
              <input
                value={form.link_url}
                onChange={(event) => setForm({ ...form, link_url: event.target.value })}
                placeholder="https://..."
              />
            </label>

            <label>
              <span>Display Order</span>
              <input
                type="number"
                value={form.display_order}
                onChange={(event) => setForm({ ...form, display_order: event.target.value })}
              />
            </label>

            <label className="ads-toggle-row">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
              />
              <span>Active</span>
            </label>

            <label>
              <span>Start Time</span>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(event) => setForm({ ...form, starts_at: event.target.value })}
              />
            </label>

            <label>
              <span>End Time</span>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(event) => setForm({ ...form, ends_at: event.target.value })}
              />
            </label>
          </div>

          <div className="ads-placement-group">
            <span>Placements</span>
            <div className="ads-placement-options">
              {PLACEMENTS.map((placement) => {
                const selected = selectedPlacements.includes(placement.id);
                return (
                  <button
                    key={placement.id}
                    type="button"
                    className={`ads-placement-chip ${selected ? 'selected' : ''}`}
                    aria-pressed={selected}
                    onClick={() => togglePlacement(placement.id)}
                  >
                    <span className="ads-placement-check" aria-hidden="true">
                      {selected ? '✓' : ''}
                    </span>
                    {placement.label}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="ads-upload-zone">
            <Upload size={24} />
            <strong>{mediaFile ? mediaFile.name : 'Choose image or video'}</strong>
            <span>JPG, PNG, WEBP, GIF, MP4, MOV, WEBM, M4V up to 30MB</span>
            <input type="file" accept="image/*,video/*" onChange={handleFileChange} />
          </label>
        </div>

        <div className="ads-preview-panel">
          <div className="ads-preview-frame">
            {mediaPreview ? (
              previewType === 'video' ? (
                <video src={mediaPreview} controls muted playsInline />
              ) : (
                <img src={mediaPreview} alt={form.title || 'Ad preview'} />
              )
            ) : (
              <div className="ads-preview-empty">
                <ImageIcon size={28} />
                <span>No media selected</span>
              </div>
            )}
          </div>
          <div className="ads-editor-actions">
            {editingAd ? (
              <button type="button" className="ads-secondary-btn" onClick={resetForm}>
                <X size={16} />
                Cancel
              </button>
            ) : null}
            <button type="submit" className="ads-primary-btn" disabled={saving}>
              {saving ? 'Saving...' : editingAd ? 'Update Ad' : 'Publish Ad'}
            </button>
          </div>
        </div>
      </form>

      <div className="ads-list-card">
        <div className="ads-list-header">
          <h2>Published Ads</h2>
          <span>{ads.length} total</span>
        </div>

        {loading ? (
          <div className="ads-empty-state">Loading ads...</div>
        ) : ads.length === 0 ? (
          <div className="ads-empty-state">No ads uploaded yet.</div>
        ) : (
          <div className="ads-table-wrap">
            <table className="ads-table">
              <thead>
                <tr>
                  <th>Media</th>
                  <th>Title</th>
                  <th>Placements</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => {
                  const mediaUrl = ad.mediaUrl || ad.media_url;
                  const mediaType = getMediaType(ad);
                  const isActive = Boolean(ad.isActive ?? ad.is_active);
                  return (
                    <tr key={ad.id}>
                      <td>
                        <div className="ads-media-thumb">
                          {mediaType === 'video' ? (
                            <video src={mediaUrl} muted playsInline />
                          ) : (
                            <img src={mediaUrl} alt={ad.title || 'Ad'} />
                          )}
                          <span>{mediaType === 'video' ? <Video size={13} /> : <ImageIcon size={13} />}</span>
                        </div>
                      </td>
                      <td>
                        <strong>{ad.title || 'Untitled ad'}</strong>
                        {ad.linkUrl || ad.link_url ? (
                          <p className="ads-link">{ad.linkUrl || ad.link_url}</p>
                        ) : null}
                      </td>
                      <td>
                        <div className="ads-chip-list">
                          {(ad.placements || []).map((placement) => (
                            <span key={placement}>{placement}</span>
                          ))}
                        </div>
                      </td>
                      <td>{ad.displayOrder ?? ad.display_order ?? 0}</td>
                      <td>
                        <button
                          type="button"
                          className={`ads-status-pill ${isActive ? 'active' : 'inactive'}`}
                          onClick={() => toggleActive(ad)}
                        >
                          {isActive ? 'Active' : 'Paused'}
                        </button>
                      </td>
                      <td>
                        <div className="ads-row-actions">
                          <button type="button" onClick={() => editAd(ad)} title="Edit ad">
                            <Edit2 size={16} />
                          </button>
                          <button type="button" onClick={() => deleteAd(ad)} title="Delete ad">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAds;
